import { useState } from 'react';
import { Flex, Text, Box, Image, AccordionListItem, ListItem } from '@opendoor/bricks/core';
import { colors } from '@opendoor/bricks/theme/eero';

import { ColorScheme } from '@opendoor/bricks/system/theme';

interface FinalOfferContentTitleProps {
  title: string;
  value: string;
  valueColor?: ColorScheme;
  valueHint?: string;
  valueHighlightIcon?: string;
  isOpen?: boolean;
  bold?: boolean;
}

interface FinalOfferContentBodyProps {
  body?: string;
  bodyExtra?: JSX.Element;
  inModal?: boolean;
}

export interface FinalOfferContentItemProps
  extends FinalOfferContentTitleProps,
    FinalOfferContentBodyProps {
  analyticsName?: string;
  panelId: string;
  isAccordion?: boolean;
  isLastItem?: boolean;
}

// component-test-check-ignore
/**
 * FinalOfferContentItem
 */
export const FinalOfferContentItem: React.FC<FinalOfferContentItemProps> = ({
  analyticsName,
  panelId,
  title,
  value,
  valueHint,
  valueHighlightIcon,
  body,
  bodyExtra,
  isAccordion,
  isLastItem,
  bold,
  valueColor,
  inModal,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Box data-testid="final-offer-content-item">
      {isAccordion && body ? (
        <AccordionListItem
          analyticsName={analyticsName}
          title={
            <FinalOfferContentTitle
              title={title}
              valueHint={valueHint}
              value={value}
              valueColor={valueColor}
              bold={bold}
              valueHighlightIcon={valueHighlightIcon}
              isOpen={isOpen}
            />
          }
          panelId={panelId}
          variant="secondary"
          paddingInline={[4, null, 6]}
          noPadding
          hasBorder
          onToggle={() => {
            setIsOpen((prevIsOpen) => !prevIsOpen);
          }}
        >
          <FinalOfferContentBody body={body} bodyExtra={bodyExtra} inModal={inModal} />
        </AccordionListItem>
      ) : (
        <ListItem
          // need to use non standard spacing to align content with Accordion components
          pl={['18px', null, '26px']}
          pr={['30px', null, '58px']}
          tabIndex={0}
          _focus={{
            outline: 'none',
            borderRadius: 'semiRounded',
            boxShadow: 'none',
            border: `2px solid ${colors.neutrals100}`,
          }}
        >
          <FinalOfferContentTitle
            title={title}
            valueHint={valueHint}
            value={value}
            bold={bold}
            valueHighlightIcon={valueHighlightIcon}
            valueColor={valueColor}
          />
        </ListItem>
      )}
      {!isLastItem && <Box borderBottom={`1px solid ${colors.neutrals30}`} mx={6} />}
    </Box>
  );
};

/*
 * FinalOfferContentTitle
 */
const FinalOfferContentTitle: React.FC<FinalOfferContentTitleProps> = ({
  title,
  value,
  valueColor,
  valueHint,
  valueHighlightIcon,
  isOpen,
  bold,
}) => {
  return (
    <Flex
      justifyContent="space-between"
      alignItems="center"
      gridGap="3"
      width="100%"
      py={5}
      pl={2}
      lineHeight="s1"
      fontSize="s1"
    >
      <Text
        fontWeight={isOpen || bold ? 'semibold' : 'regular'}
        data-testid="final-offer-content-item-title"
      >
        {title}
      </Text>
      <Flex justifyContent="flex-end">
        {valueHint && (
          <Text
            color="neutrals70"
            mr={5}
            fontWeight={isOpen || bold ? 'semibold' : 'regular'}
            data-testid="final-offer-content-item-value-hint"
          >
            {valueHint}
          </Text>
        )}
        {valueHighlightIcon && <Image src={valueHighlightIcon} alt="value-highlight-icon" mr={3} />}
        <Text
          color={valueColor}
          fontWeight={isOpen || bold ? 'semibold' : 'regular'}
          data-testid="final-offer-content-item-value"
        >
          {value}
        </Text>
      </Flex>
    </Flex>
  );
};

/*
 * FinalOfferContentBody
 */
const FinalOfferContentBody: React.FC<FinalOfferContentBodyProps> = ({
  body,
  bodyExtra,
  inModal,
}) => {
  return (
    <Flex
      flexDirection={inModal ? 'column' : ['column', null, 'row']}
      pb={5}
      pr={3}
      gridGap="5"
      data-testid="final-offer-content-item-body"
      lineHeight="s1"
    >
      <Text fontSize="s1" width={inModal ? '100%' : ['100%', null, '50%']}>
        {body}
      </Text>
      {bodyExtra && <Box width={inModal ? '100%' : ['100%', null, '50%']}>{bodyExtra}</Box>}
    </Flex>
  );
};
